/**
 * Single search-result row shared by both the desktop dropdown and the
 * mobile full-screen overlay. Variant tweaks paddings + hover styles to
 * match the surrounding container without duplicating the result markup.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { getTypeColor, getTypeIcon, highlightMatch } from "./searchHelpers";

function statusClasses(status) {
  if (status === "confirmed" || status === "available" || status === "approved") {
    return "bg-green-500/10 text-green-400";
  }
  if (status === "pending" || status === "open") {
    return "bg-yellow-500/10 text-yellow-400";
  }
  if (status === "in_flight" || status === "in_progress") {
    return "bg-blue-500/10 text-blue-400";
  }
  if (status === "cancelled" || status === "rejected") {
    return "bg-red-500/10 text-red-400";
  }
  return "bg-slate-500/10 text-slate-400";
}

export default function SearchResultRow({ result, query, variant, onClick }) {
  const { t } = useTranslation();
  const getFieldLabel = (field) => t(`fieldLabels.${field}`, { defaultValue: field });

  const desktop = variant === "desktop";

  return (
    <button
      onClick={onClick}
      className={
        desktop
          ? "w-full px-4 py-3 text-left hover:bg-slate-800/50 transition-colors duration-200 border-b border-slate-800/50 group"
          : "w-full px-4 py-3 text-left border-b border-slate-800/50 active:bg-slate-800/50"
      }
    >
      <div className={desktop ? "flex items-start gap-3" : "flex items-center gap-3"}>
        <div
          className={`p-1.5 rounded-lg ${getTypeColor(result.type)} ${desktop ? "mt-0.5" : ""} flex-shrink-0`}
        >
          {getTypeIcon(result.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm text-white font-medium truncate ${desktop ? "group-hover:text-white transition-colors" : ""}`}
          >
            {result.title}
          </p>
          <p className="text-xs text-slate-400 truncate">{result.subtitle}</p>
          {desktop && result.matchedField && result.matchedValue && (
            <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
              <span className="text-slate-400">{t("search.match")}:</span>
              <span className="text-slate-400">{getFieldLabel(result.matchedField)}</span>
              <span className="text-slate-600">=</span>
              <span className="text-slate-300 truncate max-w-[180px]">
                &quot;{highlightMatch(result.matchedValue, query)}&quot;
              </span>
            </p>
          )}
        </div>
        <div className={desktop ? "flex flex-col items-end gap-1 flex-shrink-0" : "flex-shrink-0"}>
          {result.status && (
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusClasses(result.status)}`}
            >
              {result.status === "in_flight"
                ? t("status.in_progress")
                : t(`status.${result.status}`, {
                    defaultValue: (result.status || "").replace(/_/g, " "),
                  })}
            </span>
          )}
          {desktop && (
            <span className="text-[9px] text-slate-400 capitalize">{result.matchType}</span>
          )}
        </div>
      </div>
    </button>
  );
}
